// View models for the secondary market (Figma 34407:180693). Shared by every
// marketplace sub-module: browse (details + sellers), invest (buy), sell
// (create listing) and orders (orders & listings management). Built from the
// investor-service wire DTOs by ../api/tradingParser.ts — money arrives as
// decimal strings and is parsed to numbers here; nullable server fields stay
// `null` (never invented).
//
// Glossary — keep these words distinct:
//   - MarketProperty — a property that trades on the secondary market.
//   - SellerListing  — one investor's open offer to sell tokens of it.
//   - SecondaryOrder — a buy (or the sell side of a fill) against a listing.
//   - MyListing      — a listing the signed-in investor created.

/** One charged fee line (platform, DLD, performance…), amounts in AED. */
export interface TradeFeeLine {
  code: string
  label: string
  /** Display rate ("2%"), null for a flat fee. */
  rate: string | null
  amount: number
  vatAmount: number
}

export interface TradingPagination {
  page: number
  limit: number
  totalItems: number
  totalPages: number
}

export interface TradingPage<T> {
  rows: T[]
  pagination: TradingPagination
}

// ── Properties ────────────────────────────────────────────────────────────

/** A marketplace grid card — one property with ≥1 open listing. */
export interface MarketPropertyCard {
  /** Property id — the marketplace's key (routes, holdings, listings). */
  id: string
  /** Primary offering id — the rich detail (media, yields) lives under it. */
  offeringId: string
  title: string
  location: string
  /** Cover photo URL; '' when the property has none. */
  image: string
  propertyType?: string
  bedrooms?: number
  bathrooms?: number
  area?: string
  totalTokens: number
  /** Latest independent valuation per token; null when there is none. */
  valuation: number | null
  valuationDate: string | null
  lowestPrice: number
  averagePrice: number
  /** Σ tokens remaining across open listings. */
  tokensAvailable: number
  tokensListed: number
  tokensSold: number
  sellerCount: number
  listingCount: number
  /** Max discount vs valuation, positive = discount; null without a valuation. */
  bestDiscountPct: number | null
  /** The signed-in investor holds tokens in this property. */
  isOwned: boolean
  tradingPaused: boolean
  tradingPausedReason: string | null
  latestListedAt: string
}

/** Trading detail for one property (the details page, buy + sell headers). */
export interface MarketProperty extends MarketPropertyCard {
  isTradable: boolean
  /** Permitted band, ± % of valuation ("15"). */
  bandPct: number
  /** Permitted price band per token; null without a valuation. */
  bandMin: number | null
  bandMax: number | null
  /** Minimum order value in AED. */
  minOrderAmount: number
  ownershipCapPct: number
  ownershipCapTokens: number
  /** Tokens the signed-in investor holds (0 if none). */
  tokensHeld: number
}

// ── Listings (buy side) ───────────────────────────────────────────────────

export interface SellerListing {
  id: string
  /** Human reference ("SM-LST-2418") — what the investor reads. */
  reference: string
  propertyId: string
  pricePerToken: number
  tokensAvailable: number
  /** Signed % vs valuation — negative is below (a discount); null without a valuation. */
  variancePct: number | null
  /** ceil(minOrderAmount / price), capped at tokensAvailable. */
  minPurchaseTokens: number
  /** Minimum order value in AED. */
  minOrderAmount: number
  listedAt: string
  /** The investor is the seller — Buy is disabled (BR-SM-07). */
  isOwnListing: boolean
}

export interface SellerListingDetail extends SellerListing {
  propertyTitle: string
  offeringId: string
}

/** Server-priced buy quote for a token count on one listing. */
export interface BuyQuote {
  listingId: string
  tokens: number
  pricePerToken: number
  investmentValue: number
  platformFee: number
  dldFee: number
  vat: number
  totalPayable: number
  fees: TradeFeeLine[]
  minTokens: number
  /** min(tokensAvailable, ownership-cap headroom). */
  maxTokens: number
  tokensAvailable: number
  tokensHeld: number
  ownershipCapTokens: number
  walletAvailableBalance: number
  isSufficientBalance: boolean
  /** Business-rule violations (error codes), empty when the quote is buyable. */
  errors: string[]
}

/** Exactly one of the two — `amount` converts to floor(amount / price). */
export type BuyQuoteRequest = { tokenQuantity: number } | { amount: number }

// ── Orders ────────────────────────────────────────────────────────────────

export const SECONDARY_ORDER_STATUS = {
  PENDING: 'pending',
  AWAITING_DISTRIBUTION: 'awaiting_distribution',
  TOKENS_DISTRIBUTED: 'tokens_distributed',
  AWAITING_REGISTRATION: 'awaiting_registration',
  AWAITING_APPROVAL: 'awaiting_approval',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
} as const

export type SecondaryOrderStatus =
  (typeof SECONDARY_ORDER_STATUS)[keyof typeof SECONDARY_ORDER_STATUS]

export type SecondaryOrderSide = 'buy' | 'sell'

/**
 * Settlement tracker steps, in order — the happy path of a trade:
 * Order Placed → Processing Transfer → Transfer Complete → Registering with
 * DLD → Awaiting DLD Approval → Completed.
 */
export const SETTLEMENT_STEPS = [
  SECONDARY_ORDER_STATUS.PENDING,
  SECONDARY_ORDER_STATUS.AWAITING_DISTRIBUTION,
  SECONDARY_ORDER_STATUS.TOKENS_DISTRIBUTED,
  SECONDARY_ORDER_STATUS.AWAITING_REGISTRATION,
  SECONDARY_ORDER_STATUS.AWAITING_APPROVAL,
  SECONDARY_ORDER_STATUS.COMPLETED,
] as const
export type SettlementStep = (typeof SETTLEMENT_STEPS)[number]

/** One row of the investor's orders list (buy or sell side of a trade). */
export interface SecondaryOrder {
  id: string
  /** Trade reference ("SM-TRD-…") — what the investor reads. */
  reference: string
  side: SecondaryOrderSide
  status: SecondaryOrderStatus
  statusLabel: string
  propertyId: string
  propertyTitle: string
  /** Cover photo URL; '' when none. */
  image: string
  listingId: string
  listingReference: string
  tokens: number
  pricePerToken: number
  investmentValue: number
  /** Buy side only. */
  totalPayable: number | null
  /** Sell side only — an estimate until the trade completes. */
  netProceeds: number | null
  /** Buy side, still `pending` and not yet submitted for transfer. */
  isCancellable: boolean
  placedAt: string
  updatedAt: string
}

export interface SecondaryOrderDetail extends SecondaryOrder {
  /** Buy side: price vs valuation at the time of the order. */
  variancePct: number | null
  /** Buy side: ownership share after the order settles. */
  ownershipPct: number | null
  platformFee: number
  dldFee: number | null
  performanceFee: number | null
  vat: number
  /** Only the investor's own side of the trade. */
  fees: TradeFeeLine[]
  statusReason: string | null
  /** ISO timestamp per reached step (first time reached). */
  stepDates: Partial<Record<SettlementStep, string>>
  /** Step where a rejected trade stopped. */
  failedStep?: SettlementStep
  completedAt: string | null
  cancelledAt: string | null
  rejectedAt: string | null
}

// ── My listings (sell side) ───────────────────────────────────────────────

export const MY_LISTING_STATUS = {
  UNDER_REVIEW: 'under_review',
  LIVE: 'live',
  PARTIALLY_FILLED: 'partially_filled',
  FULLY_FILLED: 'fully_filled',
  SUSPENDED: 'suspended',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
} as const

export type MyListingStatus = (typeof MY_LISTING_STATUS)[keyof typeof MY_LISTING_STATUS]

export interface ListingFill {
  /** Trade reference. */
  id: string
  /** Masked buyer handle, e.g. "Buyer #4F2A" — never the identity. */
  buyer: string
  tokens: number
  status: SecondaryOrderStatus
  statusLabel: string
  date: string
  completedAt: string | null
}

export interface ListingHistoryEntry {
  status: MyListingStatus
  at: string
  actor: 'investor' | 'admin' | 'system'
  reason: string | null
}

export interface MyListing {
  id: string
  /** Human reference ("SM-LST-2418"). */
  reference: string
  propertyId: string
  propertyTitle: string
  /** Cover photo URL; '' when none. */
  image: string
  status: MyListingStatus
  statusReason: string | null
  rejectionReason: string | null
  tokensListed: number
  tokensSold: number
  /** Tokens in a buy order that is still settling. */
  tokensInActiveOrders: number
  tokensRemaining: number
  pricePerToken: number
  /** Vs the current latest valuation. */
  variancePct: number | null
  bandMin: number
  bandMax: number
  /** Weighted-average cost per token — basis for the performance fee. */
  acquisitionBasis: number
  /** Σ net proceeds of completed trades. */
  proceedsToDate: number
  createdAt: string
  publishedAt?: string
  expiresAt?: string
  closedAt?: string
  canCancel: boolean
  canRenew: boolean
  renewalWindowOpensAt: string | null
}

export interface MyListingDetail extends MyListing {
  fills: ListingFill[]
  history: ListingHistoryEntry[]
}

// ── Sell (create listing) ─────────────────────────────────────────────────

/** Exit overview + eligibility for listing a holding. */
export interface SellOverview {
  propertyId: string
  offeringId: string
  propertyTitle: string
  image: string
  /** False → the property doesn't trade; the UI hides Sell Tokens. */
  isTradable: boolean
  canSell: boolean
  /** Error codes explaining why `canSell` is false. */
  ineligibleReasons: string[]
  tokensHeld: number
  /** Locked in open listings. */
  tokensLocked: number
  tokensSellable: number
  tokensInLockIn: number
  /** Earliest date more tokens unlock. */
  lockInEndsAt: string | null
  /** Weighted-average cost per token (provisional). */
  averageCost: number
  valuation: number | null
  valuationDate: string | null
  appreciationPerToken: number | null
  appreciationPct: number | null
  bandPct: number
  bandMin: number | null
  bandMax: number | null
  minListingAmount: number
  /** held × valuation < minimum — the investor may list everything they hold. */
  isSellAllRemainingEligible: boolean
}

/** Server-priced proceeds for listing `tokens` at `pricePerToken`. */
export interface SellQuote {
  tokens: number
  pricePerToken: number
  grossAmount: number
  platformFee: number
  performanceFee: number
  vat: number
  netProceeds: number
  fees: TradeFeeLine[]
  variancePct: number | null
  isWithinBand: boolean
  isSellAllRemaining: boolean
  errors: string[]
}

// ── Request inputs ────────────────────────────────────────────────────────

export const MARKET_PROPERTY_SORT = {
  PRICE_LOWEST: 'price_lowest',
  PRICE_HIGHEST: 'price_highest',
  NEWEST: 'newest',
  MOST_AVAILABLE: 'most_available',
} as const

export type MarketPropertySort = (typeof MARKET_PROPERTY_SORT)[keyof typeof MARKET_PROPERTY_SORT]

/** Server-side search / filter / sort for the marketplace grid. */
export interface MarketPropertyQuery {
  search?: string
  propertyTypes?: string[]
  locations?: string[]
  /** Per-token price range, AED. */
  priceMin?: number
  priceMax?: number
  tokensMin?: number
  tokensMax?: number
  /** Signed % vs valuation. */
  varianceMin?: number
  varianceMax?: number
  /** Only properties the investor already holds ("Properties I own"). */
  ownedOnly?: boolean
  sort?: MarketPropertySort
}

export interface PlaceSecondaryOrderInput {
  listingId: string
  tokens: number
  /** One per submit attempt; a retry of the same attempt reuses it. */
  idempotencyKey: string
  termsAccepted: true
}

export interface CreateListingInput {
  propertyId: string
  tokens: number
  pricePerToken: number
  /** The investor acknowledged the dividend-timing notice. */
  dividendNoticeAccepted: true
  termsAccepted: true
  idempotencyKey: string
}

/** Cancel / renew — the target plus the attempt's idempotency key. */
export interface IdempotentActionInput {
  id: string
  idempotencyKey: string
}
